import type { CreatureTone } from "./palette.ts";

/**
 * Papéis visuais da malha monocromática (doc 09, §1.3): cada objeto da cena tem um papel, e o papel
 * diz em que faixa de cinza ele fica e quanto neon pode emitir. É a hierarquia do guia (§9 e §41) como
 * dado: o ambiente nunca compete com o combate.
 *
 * Fase 15 (ADR 0028, referência `in-game.png`): facetas limpas, sem arestas nem nós em nenhum papel
 * (as arestas na cor da classe da Fase 13 saíram).
 *
 * Valores acima de 1 no acento são HDR (o bloom só pega o que passa de 1, F52); só arma, núcleo e
 * efeitos chegam lá.
 */
export interface VisualRoleDef {
  /** Faixa de cinza (fração de branco) para onde a luminância da cor de vértice é remapeada. */
  readonly gray: readonly [min: number, max: number];
  /** Intensidade do acento em repouso, na cor do dono (0 = sem neon). */
  readonly accent: number;
  /** Intensidade no pico (preparação do golpe, janela, impacto); igual a `accent` se não pulsa. */
  readonly accentPeak: number;
  /** Rim de Fresnel na cor do dono. */
  readonly rim: number;
}

export const VISUAL_ROLES = {
  /** Montanhas distantes, céu e nuvens low-poly. */
  fundo: { gray: [0.4, 0.6], accent: 0, accentPeak: 0, rim: 0 },
  /** Chão, praias e falésias; o valor muda por bioma dentro da faixa. */
  terreno: { gray: [0.22, 0.42], accent: 0, accentPeak: 0, rim: 0 },
  /** Árvores e arbustos. */
  vegetacao: { gray: [0.25, 0.43], accent: 0, accentPeak: 0, rim: 0 },
  /** Construções, estações, POIs e o Barco. */
  estrutura: { gray: [0.4, 0.65], accent: 0, accentPeak: 0, rim: 0 },
  /** Baús, itens no chão, Menires, Lápides, Estátua da Fênix e gemas: acento branco perto. */
  interagivel: { gray: [0.6, 0.9], accent: 0.2, accentPeak: 0.8, rim: 0.2 },
  /**
   * Baús de saque ainda não abertos (Fase 15): a peça inteira tinge de dourado (a forma continua legível)
   * e pulsa devagar até o HDR; o rim forte na silhueta passa do limiar do bloom sempre (um halo que se vê
   * de longe). Sem arestas nem nós.
   */
  tesouro: { gray: [0.25, 0.5], accent: 0.6, accentPeak: 1.4, rim: 2.4 },
  /**
   * Corpo dos jogadores: acento nas costuras (máscara de tinta) e rim na cor da classe. Interior
   * escurecido (pesquisa 11, era [0.45, 0.75]): o herói lê como vulto escuro com energia contida,
   * como nas referências; rosto, roupa e arma continuam legíveis sob a luz real.
   */
  heroi: { gray: [0.32, 0.58], accent: 0.15, accentPeak: 0.35, rim: 0.25 },
  /** Arma na mão, flechas em voo e rastro. */
  "arma-heroi": { gray: [0.05, 0.1], accent: 3.5, accentPeak: 3.5, rim: 0 },
  /** Corpo dos monstros: sem acento na face (a máscara deles é a da variante, não costura); rim em laranja. */
  inimigo: { gray: [0.1, 0.25], accent: 0, accentPeak: 0, rim: 0.35 },
  /**
   * Fauna neutra (Cervo, Ouriço, a capivara): sem acento nem rim (doc 09 §1.3, F75). O corpo animado acende em
   * laranja quando ataca (`creatureTone(…, hostile)`); o boneco procedural (longe ou sem modelo) fica neutro.
   */
  fauna: { gray: [0.3, 0.55], accent: 0, accentPeak: 0, rim: 0 },
  /** Olhos e núcleo dos monstros: acende na preparação do golpe. */
  "nucleo-inimigo": { gray: [0.02, 0.02], accent: 4, accentPeak: 6, rim: 0 },
  /** Corpo dos chefes, Guardiões e Dragão. */
  chefe: { gray: [0.08, 0.2], accent: 0.4, accentPeak: 0.4, rim: 0.4 },
  /** Núcleo dos chefes. */
  "nucleo-chefe": { gray: [0.02, 0.02], accent: 5, accentPeak: 7, rim: 0 },
  /** Impactos, skills e telegrafia (sem corpo: só luz). */
  fx: { gray: [0, 0], accent: 2, accentPeak: 5, rim: 0 },
} as const satisfies Record<string, VisualRoleDef>;

export type VisualRole = keyof typeof VISUAL_ROLES;

export const VISUAL_ROLE_IDS = Object.keys(VISUAL_ROLES) as VisualRole[];

/** Papéis do corpo e da peça acesa de uma criatura pelo tom (F60/F75): a fauna neutra não tem núcleo aceso. */
export function toneRoles(tone: CreatureTone): { body: VisualRole; core: VisualRole } {
  if (tone === "chefe") return { body: "chefe", core: "nucleo-chefe" };
  if (tone === "inimigo") return { body: "inimigo", core: "nucleo-inimigo" };
  return { body: "fauna", core: "fauna" };
}
